// Survey UI app
//
//  Copyright (c) 2025 MX8 Inc
//  All rights reserved.
//
//  This software is confidential and proprietary information of MX8.
//  You shall not disclose such Confidential Information and shall use it only
//  in accordance with the terms of the agreement you entered into with MX8.
import { InputAdornment, TextField } from "@mui/material";
import React, { useMemo } from "react";
import "react-phone-input-2/lib/style.css";
import { useThemeContext } from "../../../globalContext/global-context";
import { CommonTextInputProps } from "../../../util/types";
import { inputStyle } from "./style";

const CommonTextInput: React.FC<CommonTextInputProps> = ({
  isType,
  isLandScapeMode,
  handleKeyDown,
  value,
  handleChange,
  index,
  inputRefs,
  source,
  customPlaceholder,
  inputPrefix,
  inputSuffix,
  accessibleLabel: explicitAccessibleLabel,
  setKeyBoardOpenClose,
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const hasInputAdornment = Boolean(inputPrefix || inputSuffix);

  const memoizedInputStyle = useMemo(
    () => inputStyle(theme, isLandScapeMode, hasInputAdornment),
    [theme, isLandScapeMode, hasInputAdornment],
  );
  const accessibleLabel = [
    inputPrefix && `Prefix ${inputPrefix}`,
    customPlaceholder,
    inputSuffix && `Suffix ${inputSuffix}`,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <TextField
      onPaste={(e) => e.preventDefault()}
      data-testid={source}
      autoFocus={isType === "text_list" ? index === 0 : true}
      /* c8 ignore next 1 */
      onFocus={() => setKeyBoardOpenClose(true)}
      /* c8 ignore start */
      onBlur={() => setKeyBoardOpenClose(false)}
      /* c8 ignore end */
      placeholder={customPlaceholder}
      value={value}
      onChange={(e) => handleChange(e, index)}
      inputRef={(el) => {
        /* c8 ignore next */
        if (inputRefs?.current && index !== undefined) {
          inputRefs.current[index] = el;
        }
      }} // Assign ref to each input
      multiline
      slotProps={{
        input: {
          startAdornment: inputPrefix ? (
            <InputAdornment position="start">{inputPrefix}</InputAdornment>
          ) : undefined,
          endAdornment: inputSuffix ? (
            <InputAdornment position="end">{inputSuffix}</InputAdornment>
          ) : undefined,
        },
        htmlInput: {
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(e, index),
          inputMode: isType === "numeric" ? "numeric" : "text",
          autoCorrect: "off",
          "aria-label":
            explicitAccessibleLabel ??
            (inputPrefix || inputSuffix ? accessibleLabel : undefined),
        },
      }}
      sx={memoizedInputStyle}
      size="small"
      maxRows={10}
    />
  );
};

export default CommonTextInput;
